import type { AudioProcessor } from './audio';
import {
  PITCH_CHANGE_THRESHOLD,
  STABILIZATION_MS,
  TOTAL_KEYS,
} from './constants';

export interface KeyData {
  keyNum: number;
  frequency: number;
  timestamp: number;
}

export type DetectorCallback = (event: DetectorEvent) => void;

export type DetectorEvent =
  | { type: 'key-detected'; keyNum: number; frequency: number }
  | { type: 'pitch-update'; frequency: number | null }
  | { type: 'complete' }
  | { type: 'undo'; keyNum: number };

export interface Detector {
  start(): void;
  stop(): void;
  pause(): void;
  resume(): void;
  undo(): void;
  isPaused(): boolean;
  getCurrentKey(): number;
  getRecordedData(): KeyData[];
  isComplete(): boolean;
}

export function createDetector(
  processor: AudioProcessor,
  callback: DetectorCallback,
): Detector {
  const recordedData: KeyData[] = [];
  let currentKey = 1;
  let paused = false;
  let animFrameId: number | null = null;

  // Pitch tracking state
  let lastStableFreq: number | null = null;
  let candidateFreq: number | null = null;
  let candidateStartTime: number = 0;

  function processFrame(): void {
    if (paused || !processor.isActive()) {
      animFrameId = requestAnimationFrame(processFrame);
      return;
    }

    const freq = processor.getFrequency();
    callback({ type: 'pitch-update', frequency: freq });

    if (freq !== null && currentKey <= TOTAL_KEYS) {
      if (lastStableFreq === null) {
        // First note: accept immediately after stabilization
        if (candidateFreq === null) {
          candidateFreq = freq;
          candidateStartTime = performance.now();
        } else {
          const ratio = Math.abs(freq - candidateFreq) / candidateFreq;
          if (ratio < PITCH_CHANGE_THRESHOLD) {
            // Pitch is stable around candidate
            if (performance.now() - candidateStartTime >= STABILIZATION_MS) {
              registerKey(candidateFreq);
            }
          } else {
            // Pitch changed — reset candidate
            candidateFreq = freq;
            candidateStartTime = performance.now();
          }
        }
      } else {
        // Subsequent notes: detect pitch change from last registered note
        const changeRatio = Math.abs(freq - lastStableFreq) / lastStableFreq;

        if (changeRatio > PITCH_CHANGE_THRESHOLD) {
          // Significant pitch change detected — start tracking candidate
          if (candidateFreq === null) {
            candidateFreq = freq;
            candidateStartTime = performance.now();
          } else {
            const candidateRatio =
              Math.abs(freq - candidateFreq) / candidateFreq;
            if (candidateRatio < PITCH_CHANGE_THRESHOLD) {
              // Candidate is stable
              if (performance.now() - candidateStartTime >= STABILIZATION_MS) {
                registerKey(candidateFreq);
              }
            } else {
              // New candidate
              candidateFreq = freq;
              candidateStartTime = performance.now();
            }
          }
        } else {
          // Still hearing the same note — reset candidate
          candidateFreq = null;
        }
      }
    }

    animFrameId = requestAnimationFrame(processFrame);
  }

  function registerKey(frequency: number): void {
    if (currentKey > TOTAL_KEYS) return;

    recordedData.push({
      keyNum: currentKey,
      frequency,
      timestamp: Date.now(),
    });

    callback({ type: 'key-detected', keyNum: currentKey, frequency });

    lastStableFreq = frequency;
    candidateFreq = null;
    currentKey++;

    if (currentKey > TOTAL_KEYS) {
      callback({ type: 'complete' });
    }
  }

  return {
    start() {
      processor.start();
      paused = false;
      animFrameId = requestAnimationFrame(processFrame);
    },

    stop() {
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      processor.stop();
    },

    pause() {
      paused = true;
    },

    resume() {
      paused = false;
    },

    undo() {
      if (currentKey <= 1 || recordedData.length === 0) return;

      recordedData.pop();
      currentKey--;

      // Reset stable frequency to previous key or null
      lastStableFreq =
        recordedData.length > 0
          ? recordedData[recordedData.length - 1].frequency
          : null;
      candidateFreq = null;

      callback({ type: 'undo', keyNum: currentKey });
    },

    isPaused() {
      return paused;
    },
    getCurrentKey() {
      return currentKey;
    },
    getRecordedData() {
      return [...recordedData];
    },
    isComplete() {
      return currentKey > TOTAL_KEYS;
    },
  };
}
